import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import {
  Plus,
  Settings,
  Star,
  Trash2,
  Pencil,
  Loader2,
  Search,
  Sparkles,
  Music,
  Video,
  Image as ImageIcon,
  FileText,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchPerkCategories,
  adminListPerks,
  adminDeletePerk,
} from "../../lib/studioApi";
import PerkUploadDialog from "./PerkUploadDialog";
import PerkCategoriesDialog from "./PerkCategoriesDialog";

const ALL_KEY = "__all__";

function KindIcon({ kind, className = "w-3.5 h-3.5" }) {
  if (kind === "audio") return <Music className={className} />;
  if (kind === "video") return <Video className={className} />;
  if (kind === "image") return <ImageIcon className={className} />;
  return <FileText className={className} />;
}

function safeDate(iso) {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

export default function AdminPerksPanel() {
  const [perks, setPerks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(ALL_KEY);
  const [query, setQuery] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);

  const reloadAll = () => {
    setLoading(true);
    Promise.all([fetchPerkCategories({ asAdmin: true }), adminListPerks()])
      .then(([cats, list]) => {
        setCategories(cats);
        setPerks(list);
      })
      .catch(() => toast.error("Failed to load perks"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadAll();
  }, []);

  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return perks.filter((p) => {
      if (filter !== ALL_KEY && p.categoryId !== filter) return false;
      if (!q) return true;
      const cat = catById[p.categoryId];
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (cat?.name || "").toLowerCase().includes(q)
      );
    });
  }, [perks, filter, query, catById]);

  const onSaved = () => {
    reloadAll();
  };

  const openCreate = () => {
    setEditing(null);
    setUploadOpen(true);
  };

  const openEdit = (perk) => {
    setEditing(perk);
    setUploadOpen(true);
  };

  const handleDelete = async (perk) => {
    if (!window.confirm(`Delete "${perk.title}"?`)) return;
    try {
      await adminDeletePerk(perk.id);
      setPerks((prev) => prev.filter((p) => p.id !== perk.id));
      toast.success("Perk deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-perks-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-paul-yellow" />
            Perks library
          </h2>
          <p className="text-white/50 text-sm mt-1">
            {perks.length} perk{perks.length === 1 ? "" : "s"} ·{" "}
            {categories.filter((c) => !c.hidden).length} active categor
            {categories.filter((c) => !c.hidden).length === 1 ? "y" : "ies"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCatsOpen(true)}
            data-testid="manage-categories-btn"
            className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white gap-2"
          >
            <Settings className="w-4 h-4" />
            Categories
          </Button>
          <Button
            data-testid="add-perk-btn"
            onClick={openCreate}
            className="bg-paul-yellow hover:brightness-105 text-navy font-bold gap-2"
            style={{ color: "#0b1535" }}
          >
            <Plus className="w-4 h-4" />
            New perk
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative md:max-w-xs flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="admin-perks-search"
            placeholder="Search perks…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 bg-[#10162a] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            data-testid="admin-perks-filter"
            className="md:w-60 bg-[#10162a] border-white/10 text-white focus:ring-paul-yellow/40"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_KEY}>All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name} {c.hidden ? "(hidden)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/50">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0f1322] border border-white/5 rounded-2xl p-12 text-center">
          <Package className="w-10 h-10 mx-auto mb-3 text-white/20" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-white mb-1">
            {query || filter !== ALL_KEY ? "Nothing matches." : "No perks yet."}
          </h3>
          <p className="text-white/50 text-sm">
            {query || filter !== ALL_KEY
              ? "Try a different filter or search."
              : 'Tap "New perk" to upload your first sample pack, lesson or video.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const cat = catById[p.categoryId];
            return (
              <article
                key={p.id}
                data-testid={`admin-perk-card-${p.id}`}
                className="bg-[#0f1322] border border-white/5 rounded-2xl overflow-hidden flex flex-col"
              >
                {/* Thumb */}
                <div className="relative aspect-[16/10] w-full bg-[#0b0f1e] overflow-hidden">
                  {p.thumbnailUrl ? (
                    <img
                      src={p.thumbnailUrl}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-paul-yellow/30">
                      <Package className="w-10 h-10" strokeWidth={1.5} />
                    </div>
                  )}
                  {p.featured && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-paul-yellow text-navy text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded">
                      <Star className="w-3 h-3 fill-current" /> Featured
                    </span>
                  )}
                </div>
                {/* Body */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-paul-yellow mb-1">
                    {cat?.name || "Uncategorized"}
                  </div>
                  <h3 className="text-base font-extrabold text-white leading-tight mb-1 line-clamp-2">
                    {p.title}
                  </h3>
                  {p.description && (
                    <p className="text-xs text-white/55 line-clamp-2 mb-3">{p.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-[10px] text-white/40 mb-3">
                    <span>{safeDate(p.createdAt)}</span>
                    {(p.attachments || []).length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        ·
                        {(p.attachments || []).slice(0, 3).map((a, i) => (
                          <KindIcon key={`${a.url}-${i}`} kind={a.kind} />
                        ))}
                        {(p.attachments || []).length > 3 && (
                          <span>+{p.attachments.length - 3}</span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="mt-auto flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(p)}
                      data-testid={`admin-perk-edit-${p.id}`}
                      className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white gap-1 h-8"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </Button>
                    <button
                      type="button"
                      onClick={() => handleDelete(p)}
                      data-testid={`admin-perk-delete-${p.id}`}
                      className="ml-auto p-2 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      aria-label="Delete perk"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <PerkUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        perk={editing}
        categories={categories}
        onSaved={onSaved}
      />
      <PerkCategoriesDialog
        open={catsOpen}
        onOpenChange={setCatsOpen}
        onChanged={reloadAll}
      />
    </div>
  );
}
